import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowLeft } from 'lucide-react';
import LastUpdatedBadge from '../../components/common/LastUpdatedBadge';
import TableOfContents from '../../components/common/TableOfContents';
import useSEO from '../../hooks/useSEO';

const LAST_UPDATED = 'March 12, 2026';

export default function TermsOfServicePage() {
  const contentRef = useRef(null);
  useSEO({
    title: 'Terms of Service · U-DORM',
    description: 'U-DORM Terms of Service, including subscription billing terms: $9.99/mo or $95/yr, no auto-renew, 30-Day Money-Back Guarantee, Annual Loyalty Bonus.',
    image: '/logo.png',
  });
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F5DC] to-[#E8E4D9] dark:from-gray-900 dark:to-gray-800">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#2F4F4F] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">U</span>
              </div>
              <span className="text-xl font-bold text-[#2F4F4F] dark:text-white">U-DORM</span>
            </Link>
            <div className="flex items-center gap-3">
              <LastUpdatedBadge date={LAST_UPDATED} />
              <Link to="/">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-8">
        <TableOfContents contentRef={contentRef} />
        <div ref={contentRef} className="flex-1 min-w-0 bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 md:p-12 prose prose-gray dark:prose-invert max-w-none text-justify hyphens-auto">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Terms of Service</h1>
          <p className="text-muted-foreground mb-8">Last updated: {LAST_UPDATED}</p>

          <p>Welcome to U-DORM, a dormitory and rental property management platform ("Platform") operated by <strong>MindfulUX</strong>, a company organized and existing under the laws of the State of California, United States ("Company," "we," "us," or "our"). By accessing or using U-DORM, you agree to be bound by these Terms of Service ("Terms"). If you do not agree, do not use the Platform.</p>

          <h2>1. Definitions</h2>
          <ul>
            <li><strong>"User"</strong> means any individual or entity that creates an account or uses the Platform, including Landlords, Tenants, Property Managers, and Administrators.</li>
            <li><strong>"Landlord"</strong> means a User who owns or manages residential rental properties and uses U-DORM to manage tenants, leases, and payments.</li>
            <li><strong>"Tenant"</strong> means a User who rents or occupies a unit managed through the Platform.</li>
            <li><strong>"Property Manager"</strong> means a User authorized by a Landlord to manage properties on their behalf.</li>
            <li><strong>"Content"</strong> means all information, data, text, images, documents, and other materials uploaded, posted, or transmitted through the Platform.</li>
            <li><strong>"Services"</strong> means all features, tools, and functionalities provided through the Platform.</li>
          </ul>

          <h2>2. Eligibility</h2>
          <p>You must be at least 18 years of age (or the age of majority in your jurisdiction) to use the Platform. By creating an account, you represent and warrant that you meet this requirement and have the legal capacity to enter into these Terms.</p>

          <h2>3. Account Registration and Security</h2>
          <ul>
            <li>You must provide accurate, current, and complete information during registration.</li>
            <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
            <li>You must immediately notify us of any unauthorized use of your account.</li>
            <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
            <li>One person or entity may not maintain multiple accounts without our prior written consent.</li>
          </ul>

          <h2>4. Description of Services</h2>
          <p>U-DORM provides a cloud-based platform that enables:</p>
          <ul>
            <li>Property and unit management for landlords</li>
            <li>Lease agreement creation, management, and digital acknowledgment</li>
            <li>Rent payment tracking, submission, and verification</li>
            <li>Tenant onboarding and profile management</li>
            <li>Property manager assignment and permission management</li>
            <li>Communication between landlords, property managers, and tenants</li>
            <li>Financial reporting, bookkeeping, and analytics</li>
            <li>Document storage and receipt generation</li>
          </ul>

          <h2>5. Subscription Plans and Fees</h2>
          <p>U-DORM is offered as a single, all-inclusive subscription. There are no feature tiers — every Landlord gets the same complete platform. The only choice is how often you'd like to pay. Full billing terms are in Section 5A below.</p>

          <h2>5A. Subscription Payments &amp; Billing</h2>
          <h3>5A.1 Plan &amp; Pricing</h3>
          <ul>
            <li><strong>One plan, everything included</strong> — unlimited properties, unlimited units, unlimited tenants, unlimited property managers, lease management, payment tracking, documents, in-app messaging, reports, and PDF exports.</li>
            <li><strong>Monthly:</strong> USD $9.99, billed once per 30-day period.</li>
            <li><strong>Annual:</strong> USD $95.00, billed once per 365-day period — that's about $7.92/month, saving roughly 20% versus monthly.</li>
            <li><strong>No free trial.</strong> All access requires an active paid subscription.</li>
          </ul>

          <h3>5A.2 Payment Processor</h3>
          <ul>
            <li>All subscription payments are processed by <strong>Stripe, Inc.</strong> ("Stripe"). By subscribing, you agree to Stripe's <a href="https://stripe.com/legal/end-users" target="_blank" rel="noopener noreferrer" className="text-[#C85A42] hover:underline">end-user terms</a> and <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#C85A42] hover:underline">privacy policy</a>.</li>
            <li>We never receive, store, or process your full card number, CVC, or bank credentials. Stripe handles all sensitive payment data under PCI-DSS Level 1 standards.</li>
            <li>Accepted methods include all major credit and debit cards supported by Stripe in your region.</li>
          </ul>

          <h3>5A.3 How Billing Works</h3>
          <ul>
            <li>Subscriptions <strong>do not auto-renew</strong>. Each billing period (monthly or annual) is a one-time Stripe charge that grants access for the duration of that period.</li>
            <li>Before the end of a paid period, you will receive in-app and email reminders to renew. Renewing is a single click that starts a new period from your current expiration date.</li>
            <li>If you do not renew, your subscription lapses at the period end. Your data is retained for at least 60 days so you can reactivate without loss.</li>
            <li>Because there is no auto-renew, you can stop at any time — simply don't click renew. No separate cancellation flow is required.</li>
          </ul>

          <h3>5A.4 Annual Loyalty Bonus</h3>
          <ul>
            <li>Landlords who renew their <strong>annual subscription for the second consecutive year</strong> automatically receive a complimentary <strong>+36 day extension</strong> appended to their new term (an effective ~10% bonus).</li>
            <li>The bonus is applied automatically at the moment the second annual renewal is paid — no coupon code required.</li>
            <li>The bonus is non-transferable and has no cash value. We reserve the right to modify or discontinue this loyalty program with 30 days' notice.</li>
          </ul>

          <h3>5A.5 30-Day Money-Back Guarantee</h3>
          <ul>
            <li>If you are not satisfied with U-DORM, you may request a <strong>full refund within 30 days</strong> of your first subscription payment.</li>
            <li>To request a refund, email <a href="mailto:info@mindfulux.org" className="text-[#C85A42] hover:underline">info@mindfulux.org</a> from the email address on your account. No questions asked.</li>
            <li>Refunds after the 30-day window — including for partial months or partial years of a renewed term — are at our sole discretion and are generally not provided, except where required by applicable consumer-protection law.</li>
          </ul>

          <h3>5A.6 Taxes</h3>
          <ul>
            <li>Listed prices are <strong>exclusive of all applicable taxes</strong> (VAT, GST, sales tax, or similar). Taxes, if any, are calculated by Stripe at checkout based on your billing location.</li>
            <li>You are responsible for any taxes that arise from your jurisdiction's tax laws.</li>
          </ul>

          <h3>5A.7 Price Changes</h3>
          <ul>
            <li>We may change subscription pricing from time to time. Any change will be communicated at least <strong>30 days in advance</strong> via email and in-app notice.</li>
            <li>Price changes do not affect any already-paid period. They take effect on your next renewal.</li>
          </ul>

          <h3>5A.8 Disputed Charges &amp; Chargebacks</h3>
          <ul>
            <li>If you initiate a chargeback through your card issuer for a charge that we believe was valid, we may suspend your account pending resolution and may charge a reasonable administrative fee to recover the chargeback cost.</li>
            <li>We encourage you to reach out to <a href="mailto:info@mindfulux.org" className="text-[#C85A42] hover:underline">info@mindfulux.org</a> first — most billing concerns can be resolved within 1–2 business days, and you're already covered by our 30-day money-back guarantee.</li>
          </ul>

          <h2>6. User Responsibilities</h2>
          <h3>6.1 All Users</h3>
          <ul>
            <li>Comply with all applicable local, state, national, and international laws and regulations.</li>
            <li>Use the Platform only for its intended lawful purposes.</li>
            <li>Not engage in any activity that disrupts, damages, or impairs the Platform.</li>
            <li>Not upload or transmit any malicious code, viruses, or harmful content.</li>
            <li>Not attempt to gain unauthorized access to any part of the Platform or other users' accounts.</li>
          </ul>
          <h3>6.2 Landlords</h3>
          <ul>
            <li>Ensure that all property listings and lease terms comply with applicable housing laws, fair housing regulations, and local ordinances.</li>
            <li>Accurately represent property conditions, fees, and rental terms.</li>
            <li>Handle tenant data in compliance with applicable privacy laws.</li>
            <li>Not use the Platform to discriminate against any protected class.</li>
          </ul>
          <h3>6.3 Tenants</h3>
          <ul>
            <li>Provide accurate personal and payment information.</li>
            <li>Submit legitimate payment proofs and documentation.</li>
            <li>Comply with lease terms as acknowledged through the Platform.</li>
          </ul>

          <h2>7. Lease Agreements</h2>
          <ul>
            <li>Lease agreements created through U-DORM are between the Landlord and Tenant. <strong>The Company is not a party to any lease agreement.</strong></li>
            <li>U-DORM facilitates the creation, management, and acknowledgment of leases but does not provide legal advice.</li>
            <li>Users are solely responsible for ensuring that lease terms comply with applicable laws.</li>
            <li>Digital acknowledgment of a lease through the Platform does not constitute a legally binding signature unless it meets the requirements of applicable electronic signature laws (e.g., ESIGN Act, UETA).</li>
            <li>We recommend that Users consult with a licensed attorney regarding lease terms.</li>
          </ul>

          <h2>8. Payments</h2>
          <ul>
            <li>U-DORM provides tools for tracking and verifying payments but <strong>does not process, hold, or transfer funds</strong> between Landlords and Tenants.</li>
            <li>Payment transactions occur outside the Platform through third-party payment methods (e.g., GCash, Maya, bank transfers, cash).</li>
            <li>The Company is not responsible for any payment disputes, failed transactions, or fraudulent activity between Users.</li>
            <li>Landlords are responsible for verifying payments and issuing receipts through the Platform.</li>
          </ul>

          <h2>9. Intellectual Property</h2>
          <ul>
            <li>The Platform, including its design, code, features, documentation, and branding, is owned by MindfulUX and protected by copyright, trademark, and other intellectual property laws.</li>
            <li>Users retain ownership of Content they upload but grant the Company a non-exclusive, worldwide, royalty-free license to use, store, and display such Content solely for the purpose of providing the Services.</li>
            <li>You may not copy, modify, distribute, sell, or create derivative works of the Platform without our express written consent.</li>
          </ul>

          <h2>10. Privacy and Data Protection</h2>
          <ul>
            <li>Our collection and use of personal information is governed by our <Link to="/privacy-policy" className="text-[#C85A42] hover:underline">Privacy Policy</Link>, which is incorporated into these Terms by reference.</li>
            <li>We comply with applicable data protection laws, including the California Consumer Privacy Act (CCPA), California Privacy Rights Act (CPRA), and, where applicable, the General Data Protection Regulation (GDPR).</li>
            <li>Landlords who collect tenant data through the Platform are independently responsible for their compliance with applicable privacy laws.</li>
          </ul>

          <h2>11. Disclaimer of Warranties</h2>
          <p><strong>THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY.</strong> To the fullest extent permitted by applicable law, the Company disclaims all warranties, including but not limited to:</p>
          <ul>
            <li>Implied warranties of merchantability, fitness for a particular purpose, and non-infringement.</li>
            <li>That the Platform will be uninterrupted, error-free, secure, or free of viruses or other harmful components.</li>
            <li>That the information provided through the Platform is accurate, complete, or current.</li>
            <li>That lease agreements or other documents created through the Platform comply with applicable laws.</li>
          </ul>

          <h2>12. Limitation of Liability</h2>
          <p><strong>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL MINDFULUX, ITS DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE FOR:</strong></p>
          <ul>
            <li>Any indirect, incidental, special, consequential, or punitive damages.</li>
            <li>Any loss of profits, revenue, data, or business opportunities.</li>
            <li>Any damages arising from disputes between Landlords and Tenants.</li>
            <li>Any damages resulting from unauthorized access to or alteration of your data.</li>
            <li>Any damages exceeding the total amount paid by you to the Company in the twelve (12) months preceding the claim.</li>
          </ul>

          <h2>13. Indemnification</h2>
          <p>You agree to indemnify, defend, and hold harmless MindfulUX, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising out of or related to:</p>
          <ul>
            <li>Your use of the Platform.</li>
            <li>Your violation of these Terms.</li>
            <li>Your violation of any applicable law or regulation.</li>
            <li>Any Content you upload, post, or transmit through the Platform.</li>
            <li>Any dispute between you and another User.</li>
          </ul>

          <h2>14. Termination</h2>
          <ul>
            <li>You may terminate your account at any time by contacting us or using the account deletion feature.</li>
            <li>We may suspend or terminate your account at our sole discretion, with or without notice, for any reason, including violation of these Terms.</li>
            <li>Upon termination, your right to use the Platform ceases immediately.</li>
            <li>Provisions that by their nature should survive termination shall survive, including Sections 9, 11, 12, 13, and 16.</li>
            <li>We may retain certain data as required by law or for legitimate business purposes after termination.</li>
          </ul>

          <h2>15. Dispute Resolution</h2>
          <h3>15.1 Informal Resolution</h3>
          <p>Before initiating any formal proceedings, the parties agree to attempt to resolve disputes informally by contacting <a href="mailto:info@mindfulux.org" className="text-[#C85A42] hover:underline">info@mindfulux.org</a>. We will attempt to resolve your concern within 30 business days.</p>
          <h3>15.2 Arbitration</h3>
          <p>If informal resolution fails, any dispute, controversy, or claim arising out of or relating to these Terms shall be settled by binding arbitration administered by the American Arbitration Association (AAA) in accordance with its Commercial Arbitration Rules. The arbitration shall take place in San Francisco, California, or remotely at the arbitrator's discretion.</p>
          <h3>15.3 Class Action Waiver</h3>
          <p><strong>YOU AGREE THAT ANY DISPUTE RESOLUTION PROCEEDINGS WILL BE CONDUCTED ONLY ON AN INDIVIDUAL BASIS AND NOT IN A CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION.</strong></p>
          <h3>15.4 Exceptions</h3>
          <p>Either party may seek injunctive or other equitable relief in any court of competent jurisdiction to prevent the actual or threatened infringement of intellectual property rights.</p>

          <h2>16. Governing Law</h2>
          <p>These Terms shall be governed by and construed in accordance with the laws of the <strong>State of California, United States of America</strong>, without regard to its conflict of law provisions. For Users outside the United States, these Terms shall be interpreted to the extent consistent with applicable local laws, and any mandatory consumer protection laws of your jurisdiction shall apply to the extent required.</p>

          <h2>17. Modifications to Terms</h2>
          <ul>
            <li>We reserve the right to modify these Terms at any time.</li>
            <li>Material changes will be communicated via email or through the Platform at least 30 days before taking effect.</li>
            <li>Your continued use of the Platform after changes take effect constitutes acceptance of the modified Terms.</li>
            <li>If you disagree with the changes, you must discontinue use of the Platform and close your account.</li>
          </ul>

          <h2>18. General Provisions</h2>
          <ul>
            <li><strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy and any other policies referenced herein, constitute the entire agreement between you and MindfulUX regarding the Platform.</li>
            <li><strong>Severability:</strong> If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.</li>
            <li><strong>Waiver:</strong> The failure of the Company to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.</li>
            <li><strong>Assignment:</strong> You may not assign your rights or obligations under these Terms without our prior written consent. We may assign our rights and obligations without restriction.</li>
            <li><strong>Force Majeure:</strong> The Company shall not be liable for any failure or delay in performance due to circumstances beyond its reasonable control.</li>
          </ul>

          <h2>19. Contact Information</h2>
          <p>If you have questions or concerns about these Terms, please contact us:</p>
          <div className="bg-muted/50 p-4 rounded-lg not-prose">
            <p className="font-semibold">MindfulUX</p>
            <p className="text-sm text-muted-foreground">California, United States</p>
            <p className="text-sm">Email: <a href="mailto:info@mindfulux.org" className="text-[#C85A42] hover:underline">info@mindfulux.org</a></p>
            <p className="text-sm">Website: <a href="https://www.udorm.com" className="text-[#C85A42] hover:underline">www.udorm.com</a></p>
          </div>

          <div className="mt-12 pt-8 border-t text-center text-sm text-muted-foreground not-prose">
            <p>&copy; {new Date().getFullYear()} MindfulUX. All rights reserved.</p>
            <div className="flex justify-center gap-4 mt-2">
              <Link to="/privacy-policy" className="text-[#C85A42] hover:underline">Privacy Policy</Link>
              <Link to="/cookie-policy" className="text-[#C85A42] hover:underline">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
